// A CRM suppression source is a POINTER at where a client keeps suppression truth inside their own
// CRM — a HubSpot list, a Salesforce campaign or field. The addresses it yields land in the ordinary
// suppression list, not somewhere separate.
//
// Shapes are generated from OrbitalX.Dto — see api-generated.d.ts. Every enum-backed field is
// declared `string` in C# and carries its [Description] value on the wire, so the unions below stay
// hand-written and are applied over the generated shape.

import type {
  CrmPickerOptionsDto as GeneratedCrmPickerOptionsDto,
  CrmSuppressionSourceCreateDto as GeneratedCrmSuppressionSourceCreateDto,
  CrmSuppressionSourceViewDto as GeneratedCrmSuppressionSourceViewDto,
} from './api-generated'

export type { CrmPickerOptionDto } from './api-generated'

/** "HubSpot" | "Salesforce". */
export type CrmSuppressionProvider = 'HubSpot' | 'Salesforce'

/** Which reader runs, and how ExternalId is interpreted. */
export type CrmSuppressionSourceKind =
  | 'HubSpot List'
  | 'Salesforce Standard Opt Out'
  | 'Salesforce Campaign'
  | 'Salesforce Field Condition'

/** What the source yields: people (addresses) or companies (domains). */
export type CrmSuppressionGrain = 'Individual' | 'Company'

/**
 * Why members are suppressed — and therefore whether removing someone from the source releases them.
 * "Actively Marketing" and "Do Not Contact" mirror the CRM; "Unsubscribed" and "Compliance" ratchet
 * and can only be cleared by an OrbitalX admin.
 */
export type CrmSuppressionReason = 'Actively Marketing' | 'Do Not Contact' | 'Unsubscribed' | 'Compliance'

/** Outcome of the last reconcile. "Permission Denied" is fixable by reconnecting; "Failed" is not. */
export type CrmSuppressionSyncStatus = 'Never Run' | 'Succeeded' | 'Failed' | 'Permission Denied'

/**
 * `ExternalId` is null for the Salesforce standard opt-out, which names nothing; `ExternalValue` is
 * set only on a field-condition source; `DisplayName` is the name as it read in the CRM when the
 * source was configured, captured so a renamed list still shows sensibly.
 */
export interface CrmSuppressionSourceViewDto
  extends Omit<GeneratedCrmSuppressionSourceViewDto, 'Provider' | 'Kind' | 'Grain' | 'Reason' | 'LastStatus'> {
  Provider: CrmSuppressionProvider
  Kind: CrmSuppressionSourceKind
  Grain: CrmSuppressionGrain
  Reason: CrmSuppressionReason
  LastStatus: CrmSuppressionSyncStatus
}

/**
 * POST body — add a source. Enum-backed fields are their description strings.
 *
 * `FieldType` is Salesforce's type for the chosen field ("boolean" or "picklist"), carried straight
 * from the picker. Only a checkbox may be saved without a match value — a picklist without one
 * builds an invalid query that would fail on every nightly run, so the API rejects it.
 */
export interface CrmSuppressionSourceCreatePayload
  extends Omit<GeneratedCrmSuppressionSourceCreateDto, 'Provider' | 'Kind' | 'Grain' | 'Reason'> {
  Provider: CrmSuppressionProvider
  Kind: CrmSuppressionSourceKind
  Grain: CrmSuppressionGrain
  Reason: CrmSuppressionReason
}

/**
 * How a picker read ended.
 *
 * Derived from CrmSuppressionSyncStatus rather than restating its literals, so the two cannot drift.
 * 'Never Run' is excluded because a picker read has by definition just run.
 *
 * 'Permission Denied' is the only one a reconnect fixes. It is separate from 'Succeeded' with no
 * options because those look identical from an empty array and mean opposite things — a portal that
 * holds no lists cannot be helped by reconnecting it, and saying otherwise sends the client round a
 * loop that ends where it started.
 */
export type CrmPickerStatus = Exclude<CrmSuppressionSyncStatus, 'Never Run'>

/** A picker feed's answer: the choices to offer, and why there are none when there are none. */
export interface CrmPickerOptionsDto extends Omit<GeneratedCrmPickerOptionsDto, 'Status'> {
  Status: CrmPickerStatus
}

/** Reasons that mirror the CRM, so removing a member releases them on the next run. */
export const MIRRORING_REASONS: CrmSuppressionReason[] = ['Actively Marketing', 'Do Not Contact']

/** True when dropping out of the source will release the member rather than leaving them suppressed. */
export function isReversible(reason: CrmSuppressionReason): boolean {
  return MIRRORING_REASONS.includes(reason)
}
